import ApprovalDialog from "@/admin/dashboard/components/approval-dialog/ApprovalDialog";
import ApprovalTypeTag from "@/components/tags/ApprovalTypeTag";
import { Button } from "@/components/ui/button";
import { ApprovalType, LeaveRequestDTO } from "@/models/leave-requests.model";
import { toDateFormatted } from "@/utils/date-format";
import { ColumnDef } from "@tanstack/react-table";
import { ArrowUpDown, Calendar, MoreHorizontal, Trash2 } from "lucide-react";
import { Sheet, SheetTrigger } from "@/components/ui/sheet";
import ViewOnlyLeaveRequest from "../ViewOnlyLeaveRequest";
import { capitalizeFirstLetter } from "@/utils/string-format";
import { useState } from "react";
import { useAppDispatch } from "@/app/hooks";
import { fetchLeaveRequests, fetchLeaveRequestsOfUser, fetchUserLeaveRequestsCount, removeLeaveRequest } from "@/features/leaveRequests/leaveRequestSlice";
import { toast } from "@/hooks/use-toast";
import { useCurrentUser } from "@/context/UserContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const ActionCell = ({
  row,
}: {
  row: any;
}) => {
  const status = row.original.status as ApprovalType;
  const dispatch = useAppDispatch();
  const { currentUser } = useCurrentUser();


  const [open, setOpen] = useState(false);

  const handleDelete = async () => {
    try {
      if (!currentUser) return;
      await dispatch(removeLeaveRequest(row.original.id));
      toast({
        title: "Leave request deleted",
        description: "The leave request has been deleted successfully.",
      });
      await dispatch(fetchLeaveRequestsOfUser({ userId: currentUser.id }))
      await dispatch(fetchUserLeaveRequestsCount(currentUser.id))
      setOpen(false);

    } catch (error: any) {
      console.error(error);
      toast({
        variant: "destructive",
        title: error.message || "Error",
        description: error.error || "An unknown error occurred",
      })
    }
  };
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-8 w-8 p-0">
          <span className="sr-only">Open menu</span>
          <MoreHorizontal />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => setOpen(true)}>
          <Calendar /> View
        </DropdownMenuItem>
        {status === ApprovalType.PENDING && (
          <DropdownMenuItem onClick={handleDelete}>
            <Trash2 />
            Delete Request
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
      <Sheet open={open} onOpenChange={setOpen}>
        <ViewOnlyLeaveRequest
          isOpen={open}
          handleDelete={handleDelete}
          requestObj={row.original}
        />
      </Sheet>
    </DropdownMenu>
  );
};

export const columns: ColumnDef<LeaveRequestDTO>[] = [
  {
    accessorFn: (row) => row.type,
    header: "Leave",
    cell: ({ row }) => {
      return <span>{capitalizeFirstLetter(row.original.type)}</span>;
    },
  },
  {
    id: "startDate",
    cell: ({ row }) => {
      return (
        <span>
          {toDateFormatted(row.original.fromDate)}{" "}
          {row.original.fromDateIsAm ? "AM" : "PM"}
        </span>
      );
    },
    accessorFn: (row) => row.fromDate,
    header: ({ column }) => {
      return (
        <div className="flex items-center gap-2">
          Start Date
          <button
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </button>
        </div>
      );
    },
  },

  {
    accessorFn: (row) => row.days,
    header: "Days",
    cell: ({ row }) => {
      return <span className="w-full">{row.original.days}</span>;
    },
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => {
      return <ApprovalTypeTag status={row.original.status} />;
    },
  },

  {
    id: "action",
    cell: ActionCell,
  },
];
